import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 002.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

export default function Login() {
  const { customer, loginEmail, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (customer) navigate("/orders");
  }, [customer, navigate]);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/orders";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        await register(form.name, form.email, form.password);
      } else {
        await loginEmail(form.email, form.password);
      }
      navigate("/orders");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[var(--brand-ink)] px-6 py-12">
      <div className="grain absolute inset-0" />
      <div className="relative w-full max-w-md" data-testid="customer-login">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft size={15} /> Back to site
        </Link>
        <div className="mb-8 flex items-center gap-3">
          <img src={LOGO_URL} alt="NLCS logo" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-display text-2xl font-extrabold text-white">NLCS</div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/50">Customer Account</div>
          </div>
        </div>

        <div className="bg-white p-8">
          <h1 className="font-display text-2xl font-bold">
            {mode === "register" ? "Create your account" : "Welcome back"}
          </h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Sign in to place orders and track their status.
          </p>

          <button
            onClick={googleSignIn}
            data-testid="google-signin-button"
            className="mt-6 flex w-full items-center justify-center gap-3 border border-black/15 py-3 text-sm font-semibold transition-colors hover:bg-black/5"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-black/10" />
            <span className="text-xs uppercase tracking-wide text-[var(--text-secondary)]">or</span>
            <span className="h-px flex-1 bg-black/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  data-testid="cust-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="cust-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="cust-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-cust-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="cust-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="cust-submit"
              className="flex w-full items-center justify-center gap-2 bg-[var(--brand-primary)] py-3.5 font-semibold text-white transition-colors hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : (
                <><Mail size={16} /> {mode === "register" ? "Create account" : "Sign in"}</>
              )}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-[var(--text-secondary)]">
            {mode === "register" ? "Already have an account?" : "New to NLCS?"}{" "}
            <button
              onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
              data-testid="toggle-auth-mode"
              className="font-semibold text-[var(--brand-primary)] hover:underline"
            >
              {mode === "register" ? "Sign in" : "Create an account"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
